import classNames from 'classnames';
import React, { useEffect } from 'react';
import { Button } from '@eventbrite/marmalade';
import styles from './Modal.module.scss';

/**
 * ── Reimplemented Modal (documented Marmalade GAP) ──────────────────────────
 *
 * eb-ui rendered the contact widget inside `@eventbrite/eds-modal`'s `Modal`
 * (a centred dialog with a title header, a body, and a primary/secondary button
 * bar driven by the widget's overlay config). Marmalade has **no modal**
 * (it is a named, documented gap — see memory.md/harness.md EDS policy), and
 * `eds-modal` is a React-16-era package that drags focus-trap/eds-button/
 * eds-notification. So we faithfully reimplement the subset the widget uses:
 *
 *   - a fixed dark overlay (eds `rgba(57,54,79,.8)` scrim)
 *   - a centred white card with a title header + divider
 *   - a scrollable content region (the eb-ui `eds-modal__content__children`)
 *   - a footer button bar (secondary + primary), wired to the overlay config
 *   - a close (X) button, Esc-to-close, and click-outside-to-close
 *
 * Styling values are extracted from the real eds.css modal cascade. The buttons
 * use Marmalade `Button` (the sanctioned shared component), NOT a reimplemented
 * button — only the modal *shell* is a gap.
 */

const VIEW_CONTAINER_CLASS = 'eds-modal__content__children';

export interface ModalButtonConfig {
    onPrimaryAction?: (() => void) | null;
    primaryText?: string | null;
    primaryType?: string | null;
    onSecondaryAction?: (() => void) | null;
    secondaryText?: string | null;
    secondaryType?: string | null;
}

export interface ModalProps extends ModalButtonConfig {
    title: string;
    isShown: boolean;
    isClosable?: boolean;
    onClose?: () => void;
    disableCloseOnClickOutside?: boolean;
    children?: React.ReactNode;
}

// eb-ui overlay config used eds Button `style` names ('fill'/'neutral'); map
// them onto Marmalade Button variants. 'fill' → primary brand action;
// 'neutral' → secondary.
const toVariant = (
    edsStyle?: string | null,
): 'primary' | 'secondary' | 'ghost' => {
    switch (edsStyle) {
        case 'fill':
            return 'primary';
        case 'neutral':
            return 'secondary';
        default:
            return 'ghost';
    }
};

export const Modal: React.FunctionComponent<ModalProps> = (props) => {
    const {
        title,
        isShown,
        isClosable = true,
        onClose,
        disableCloseOnClickOutside = false,
        onPrimaryAction,
        primaryText,
        primaryType,
        onSecondaryAction,
        secondaryText,
        secondaryType,
        children,
    } = props;

    useEffect(() => {
        if (!isShown) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && onClose) {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        // Lock the body scroll behind the modal (eb-ui eds-modal--hidden-overflow).
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [isShown, onClose]);

    if (!isShown) {
        return null;
    }

    const handleOverlayClick = () => {
        if (!disableCloseOnClickOutside && onClose) {
            onClose();
        }
    };

    const hasFooter = !!(primaryText || secondaryText);

    return (
        <div
            className={styles.overlay}
            data-spec="eds-modal-overlay"
            onClick={handleOverlayClick}
        >
            <div
                className={styles.modal}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="contact-organizer-modal-title"
                data-spec="eds-modal"
                onClick={(event) => event.stopPropagation()}
            >
                <header className={styles.header}>
                    <h2
                        id="contact-organizer-modal-title"
                        className={styles.title}
                        data-spec="eds-modal__title"
                    >
                        {title}
                    </h2>
                    {isClosable && onClose && (
                        <button
                            type="button"
                            className={styles.closeButton}
                            aria-label="Close"
                            data-spec="eds-modal__close"
                            onClick={onClose}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="16"
                                height="16"
                                aria-hidden="true"
                            >
                                <path
                                    fill="currentColor"
                                    d="M13.41 12l6.3-6.29a1 1 0 10-1.42-1.42L12 10.59l-6.29-6.3a1 1 0 00-1.42 1.42l6.3 6.29-6.3 6.29a1 1 0 101.42 1.42L12 13.41l6.29 6.3a1 1 0 001.42-1.42z"
                                />
                            </svg>
                        </button>
                    )}
                </header>

                <div className={styles.content}>
                    <div className={classNames(VIEW_CONTAINER_CLASS, styles.contentChildren)}>
                        {children}
                    </div>
                </div>

                {hasFooter && (
                    <footer className={styles.footer}>
                        {secondaryText && (
                            <span className={styles.secondaryButton}>
                                <Button
                                    variant={toVariant(secondaryType)}
                                    onClick={() =>
                                        onSecondaryAction && onSecondaryAction()
                                    }
                                    data-spec="eds-modal__secondary-button"
                                >
                                    {secondaryText}
                                </Button>
                            </span>
                        )}
                        {primaryText && (
                            <Button
                                variant={toVariant(primaryType)}
                                onClick={() =>
                                    onPrimaryAction && onPrimaryAction()
                                }
                                data-spec="eds-modal__primary-button"
                            >
                                {primaryText}
                            </Button>
                        )}
                    </footer>
                )}
            </div>
        </div>
    );
};
